/**
 * ICP Test Detail Page - View detailed test results for a single test
 * Displays all 56 ICP parameters in an organized table format
 */
import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Checkbox,
  Container,
  Title,
  Stack,
  Paper,
  Alert,
  Loader,
  Center,
  Group,
  Button,
  Text,
  Badge,
  Breadcrumbs,
  Anchor,
  Table,
  ScrollArea,
  Tooltip,
  ActionIcon,
  RingProgress,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { DateTimePicker } from '@mantine/dates';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCircleFilled,
  IconArrowUp,
  IconArrowDown,
  IconExternalLink,
  IconFilter,
  IconFilterOff,
  IconTrash,
  IconCalculator,
  IconPencil,
  IconCheck,
  IconX,
  IconTrendingUp,
  IconArrowsExchange,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { api } from '@/api/icpTests';
import { useTank } from '@/contexts/TankContext';
import { useICPTest, useElementHistory, useElementDosingRates, useUpdateICPTest, useAvailableParameterSources, useNearestManualTests } from '@/hooks/useICPTest';
import { buildAnalysisTrendUrl } from '@/utils/analysisLinks';
import { formatTimeDelta } from '@/utils/formatTimeDelta';
import { resolveStatus, toSimpleStatus } from '@/utils/icpStatus';
import { RangeBar } from '@/components/RangeBar';
import { ParameterNarrativeModal } from '@/components/ParameterNarrativeModal';
import { ParameterRowMobile, ElementHistorySection } from '@/components/ICP';
import { BELOW_DETECTION_TOOLTIP } from '@/components/ICP/ParameterRowMobile';
import { ProviderBadge } from '@/components/ICP/ProviderBadge';
import type { ReferenceRange } from '@/types/sensor';

// Sanitize user input for external URL construction - strips HTML/script tags
const sanitizeName = (name: string | undefined): string => {
  if (!name) return '';
  return name.replace(/<[^>]*>/g, '').trim();
};

// Mapping from database column names to ICP parameter codes
const DB_TO_ICP_CODE: Record<string, string> = {
  alkalinity: 'Alk',
  ph: 'pH',
  calcium: 'Ca',
  magnesium: 'Mg',
  nitrate: 'NO3',
  phosphate: 'PO4',
  potassium: 'K',
  salinity: 'Sal',
  boron: 'B',
  strontium: 'Sr',
  bromide: 'Br',
  chloride: 'Cl',
  fluoride: 'F',
  sodium: 'Na',
  sulfate: 'SO₄2-',
  sulfur: 'S',
  nitrite: 'NO2',
  phosphate_ion: 'PO43-',
  phosphorus: 'P',
  aluminum: 'Al',
  silver: 'Ag',
  arsenic: 'As',
  barium: 'Ba',
  beryllium: 'Be',
  bismuth: 'Bi',
  cadmium: 'Cd',
  cobalt: 'Co',
  chromium: 'Cr',
  copper: 'Cu',
  iron: 'Fe',
  germanium: 'Ge',
  mercury: 'Hg',
  iodine: 'I',
  lanthanum: 'La',
  lithium: 'Li',
  manganese: 'Mn',
  molybdenum: 'Mo',
  nickel: 'Ni',
  lead: 'Pb',
  antimony: 'Sb',
  selenium: 'Se',
  silicon: 'Si',
  tin: 'Sn',
  titanium: 'Ti',
  thallium: 'Tl',
  vanadium: 'V',
  tungsten: 'W',
  zinc: 'Zn',
  zirconium: 'Zr',
  calcium_carbonate: 'CaCO₃',
  attenuation_coefficient: 'AB',
  specific_gravity: 'SG',
  conductivity: 'Cond',
};

/**
 * Maps ICP database column names to analysis parameter keys.
 * Only parameters that exist in the analysis system get trend links.
 */
const ICP_TO_ANALYSIS_KEY: Record<string, string> = {
  alkalinity: 'alkalinity',
  calcium: 'ca',
  magnesium: 'mg',
  nitrate: 'no3',
  phosphate: 'po4',
  ph: 'ph',
  salinity: 'salinity',
};

/**
 * ATI quality score keys in provider_extras.quality_scores, paired with
 * their compact display labels.
 */
const QUALITY_SCORE_LABELS: Array<{ key: string; label: string }> = [
  { key: 'Major elements', label: 'Major' },
  { key: 'Minor elements', label: 'Minor' },
  { key: 'Pollutants', label: 'Pollutants' },
];

// Parameter metadata for organized display
interface ParameterMetadata {
  key: keyof ICPTestRecord;
  label: string;
  unit: string;
  decimals: number;
  category: string;
}

const PARAMETER_CATEGORIES = [
  {
    name: 'Major Parameters',
    color: 'blue',
    params: [
      { key: 'calcium' as const, label: 'Calcium', unit: 'ppm', decimals: 0 },
      { key: 'alkalinity' as const, label: 'Alkalinity', unit: 'dKH', decimals: 1 },
      { key: 'magnesium' as const, label: 'Magnesium', unit: 'ppm', decimals: 0 },
      { key: 'ph' as const, label: 'pH', unit: '', decimals: 2 },
      { key: 'salinity' as const, label: 'Salinity', unit: 'ppt', decimals: 1 },
    ] as ParameterMetadata[],
  },
  {
    name: 'Nutrients',
    color: 'green',
    params: [
      { key: 'nitrate' as const, label: 'Nitrate (NO3)', unit: 'ppm', decimals: 2 },
      { key: 'nitrite' as const, label: 'Nitrite (NO2)', unit: 'ppm', decimals: 3 },
      { key: 'phosphate' as const, label: 'Phosphate (PO4)', unit: 'ppm', decimals: 3 },
      { key: 'phosphate_ion' as const, label: 'Phosphate Ion', unit: 'ppm', decimals: 3 },
      { key: 'phosphorus' as const, label: 'Phosphorus (P)', unit: 'ppm', decimals: 3 },
      { key: 'potassium' as const, label: 'Potassium (K)', unit: 'ppm', decimals: 0 },
    ] as ParameterMetadata[],
  },
  {
    name: 'Major Elements',
    color: 'cyan',
    params: [
      { key: 'boron' as const, label: 'Boron (B)', unit: 'ppm', decimals: 1 },
      { key: 'strontium' as const, label: 'Strontium (Sr)', unit: 'ppm', decimals: 1 },
      { key: 'bromide' as const, label: 'Bromide (Br)', unit: 'ppm', decimals: 1 },
      { key: 'chloride' as const, label: 'Chloride (Cl)', unit: 'ppm', decimals: 0 },
      { key: 'fluoride' as const, label: 'Fluoride (F)', unit: 'ppm', decimals: 2 },
      { key: 'sodium' as const, label: 'Sodium (Na)', unit: 'ppm', decimals: 0 },
      { key: 'sulfate' as const, label: 'Sulfate (SO4)', unit: 'ppm', decimals: 0 },
      { key: 'sulfur' as const, label: 'Sulfur (S)', unit: 'ppm', decimals: 0 },
    ] as ParameterMetadata[],
  },
  {
    name: 'Trace Elements',
    color: 'grape',
    params: [
      { key: 'aluminum' as const, label: 'Aluminum (Al)', unit: 'ppb', decimals: 1 },
      { key: 'silver' as const, label: 'Silver (Ag)', unit: 'ppb', decimals: 2 },
      { key: 'arsenic' as const, label: 'Arsenic (As)', unit: 'ppb', decimals: 2 },
      { key: 'barium' as const, label: 'Barium (Ba)', unit: 'ppb', decimals: 1 },
      { key: 'beryllium' as const, label: 'Beryllium (Be)', unit: 'ppb', decimals: 2 },
      { key: 'bismuth' as const, label: 'Bismuth (Bi)', unit: 'ppb', decimals: 2 },
      { key: 'cadmium' as const, label: 'Cadmium (Cd)', unit: 'ppb', decimals: 2 },
      { key: 'cobalt' as const, label: 'Cobalt (Co)', unit: 'ppb', decimals: 2 },
      { key: 'chromium' as const, label: 'Chromium (Cr)', unit: 'ppb', decimals: 1 },
      { key: 'copper' as const, label: 'Copper (Cu)', unit: 'ppb', decimals: 1 },
      { key: 'iron' as const, label: 'Iron (Fe)', unit: 'ppb', decimals: 1 },
      { key: 'germanium' as const, label: 'Germanium (Ge)', unit: 'ppb', decimals: 2 },
      { key: 'mercury' as const, label: 'Mercury (Hg)', unit: 'ppb', decimals: 2 },
      { key: 'iodine' as const, label: 'Iodine (I)', unit: 'ppb', decimals: 1 },
      { key: 'lanthanum' as const, label: 'Lanthanum (La)', unit: 'ppb', decimals: 2 },
      { key: 'lithium' as const, label: 'Lithium (Li)', unit: 'ppb', decimals: 1 },
      { key: 'manganese' as const, label: 'Manganese (Mn)', unit: 'ppb', decimals: 1 },
      { key: 'molybdenum' as const, label: 'Molybdenum (Mo)', unit: 'ppb', decimals: 1 },
      { key: 'nickel' as const, label: 'Nickel (Ni)', unit: 'ppb', decimals: 1 },
      { key: 'lead' as const, label: 'Lead (Pb)', unit: 'ppb', decimals: 2 },
      { key: 'antimony' as const, label: 'Antimony (Sb)', unit: 'ppb', decimals: 2 },
      { key: 'selenium' as const, label: 'Selenium (Se)', unit: 'ppb', decimals: 2 },
      { key: 'silicon' as const, label: 'Silicon (Si)', unit: 'ppb', decimals: 1 },
      { key: 'tin' as const, label: 'Tin (Sn)', unit: 'ppb', decimals: 2 },
      { key: 'titanium' as const, label: 'Titanium (Ti)', unit: 'ppb', decimals: 2 },
      { key: 'thallium' as const, label: 'Thallium (Tl)', unit: 'ppb', decimals: 2 },
      { key: 'vanadium' as const, label: 'Vanadium (V)', unit: 'ppb', decimals: 1 },
      { key: 'tungsten' as const, label: 'Tungsten (W)', unit: 'ppb', decimals: 2 },
      { key: 'zinc' as const, label: 'Zinc (Zn)', unit: 'ppb', decimals: 1 },
      { key: 'zirconium' as const, label: 'Zirconium (Zr)', unit: 'ppb', decimals: 2 },
    ] as ParameterMetadata[],
  },
  {
    name: 'Calculated Parameters',
    color: 'orange',
    params: [
      { key: 'calcium_carbonate' as const, label: 'Calcium Carbonate', unit: 'ppm', decimals: 1 },
      { key: 'attenuation_coefficient' as const, label: 'Attenuation Coefficient', unit: 'm⁻¹', decimals: 3 },
      { key: 'specific_gravity' as const, label: 'Specific Gravity', unit: '', decimals: 4 },
      { key: 'conductivity' as const, label: 'Conductivity', unit: 'mS/cm', decimals: 1 },
    ] as ParameterMetadata[],
  },
];

// Type import
import type { ICPTestRecord } from '@/types/sensor';

export default function ICPTestDetailPage() {
  const { barcode } = useParams<{ barcode: string }>();
  const navigate = useNavigate();
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  // Fetch test data with ranges
  const { data: testData, isLoading, error } = useICPTest(barcode);

  // Fetch available parameter sources for trend linking
  const { data: availableSources } = useAvailableParameterSources();

  // Manual test comparison toggle
  const [showManualComparison, setShowManualComparison] = useState<boolean>(() => {
    return localStorage.getItem('icpDetailShowManualComparison') === 'true';
  });

  // Fetch nearest manual tests (always fetch to check availability; data used when toggle is on)
  const {
    data: manualTestsData,
    isLoading: manualTestsLoading,
  } = useNearestManualTests(barcode, true);

  const hasAnyManualData = manualTestsData && Object.keys(manualTestsData).length > 0;
  const nearestManualTests = showManualComparison ? manualTestsData : undefined;

  const handleManualComparisonToggle = () => {
    const newValue = !showManualComparison;
    setShowManualComparison(newValue);
    localStorage.setItem('icpDetailShowManualComparison', String(newValue));
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (barcodeToDelete: string) => {
      if (!selectedTank) throw new Error('No tank selected');
      return api.deleteICPTest(selectedTank.id, barcodeToDelete);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['icp-tests'] });
      notifications.show({
        title: 'Success',
        message: 'ICP test deleted successfully',
        color: 'green',
      });
      navigate('/icp-tests');
    },
    onError: (deleteError) => {
      notifications.show({
        title: 'Delete failed',
        message: deleteError instanceof Error ? deleteError.message : 'Failed to delete ICP test',
        color: 'red',
      });
    },
  });

  const handleDelete = () => {
    modals.openConfirmModal({
      title: 'Delete ICP Test',
      centered: true,
      children: (
        <Text size="sm">
          Are you sure you want to delete ICP test {barcode}? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMutation.mutate(barcode || ''),
    });
  };
  // Date edit state
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [editDate, setEditDate] = useState<Date | null>(null);

  // Update mutation
  const updateMutation = useUpdateICPTest();

  const handleDateEdit = () => {
    if (testData?.test?.test_date) {
      setEditDate(new Date(testData.test.test_date));
    }
    setIsEditingDate(true);
  };

  const handleDateSave = () => {
    if (!editDate || !barcode) return;
    updateMutation.mutate(
      { barcode, test_date: editDate.toISOString() },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Date Updated',
            message: 'Test date has been updated successfully',
            color: 'green',
          });
          setIsEditingDate(false);
        },
        onError: (err) => {
          notifications.show({
            title: 'Update Failed',
            message: err instanceof Error ? err.message : 'Failed to update test date',
            color: 'red',
          });
        },
      }
    );
  };

  const handleDateCancel = () => {
    setIsEditingDate(false);
    setEditDate(null);
  };

  const test = testData?.test;
  const referenceRanges = testData?.reference_ranges || [];

  // ----- Provider-aware display rules -----

  // Below-detection symbols ("n.d.") reported by the provider, e.g. ATI '---'
  // values. Distinct from a missing/absent parameter.
  const rawBelowDetection = test?.provider_extras?.below_detection;
  const belowDetectionSymbols = new Set<string>(
    Array.isArray(rawBelowDetection)
      ? rawBelowDetection.filter((s): s is string => typeof s === 'string')
      : []
  );
  const isBelowDetection = (paramKey: string): boolean => {
    const icpCode = DB_TO_ICP_CODE[paramKey];
    return icpCode ? belowDetectionSymbols.has(icpCode) : false;
  };

  // ATI quality scores (0-100 per group) from provider_extras.quality_scores
  const rawQualityScores = test?.provider_extras?.quality_scores;
  const qualityScores =
    rawQualityScores && typeof rawQualityScores === 'object' && !Array.isArray(rawQualityScores)
      ? (rawQualityScores as Record<string, unknown>)
      : undefined;
  const visibleQualityScores = qualityScores
    ? QUALITY_SCORE_LABELS.flatMap(({ key, label }) => {
        const score = qualityScores[key];
        return typeof score === 'number' ? [{ key, label, score }] : [];
      })
    : [];

  // Range column header: RZ/Triton report a set-point range; ATI only reports
  // an ideal value per parameter.
  const hasSetPointRanges = referenceRanges.some((r: ReferenceRange) => Boolean(r.set_point_range));
  const hasOnlyDesiredLevels =
    !hasSetPointRanges && referenceRanges.some((r: ReferenceRange) => Boolean(r.desired_level));
  const rangeColumnHeader = hasOnlyDesiredLevels ? 'Ideal Value' : 'Set Point Range';
  // When the range column already shows the ideal value, a Desired column
  // would duplicate it.
  const showDesiredColumn = !hasOnlyDesiredLevels;

  // Importance scores are a Reef Zelements-only concept
  const showImportanceColumn = test?.provider === 'reef_zelements';

  // State for narrative modal
  const [selectedParameter, setSelectedParameter] = useState<{
    name: string;
    narrative: string | null;
  } | null>(null);

  // State for out-of-range filter toggle
  const [showOutOfRangeOnly, setShowOutOfRangeOnly] = useState<boolean>(() => {
    const saved = sessionStorage.getItem('icpDetailShowOutOfRangeOnly');
    return saved === 'true';
  });

  const handleFilterToggle = () => {
    const newValue = !showOutOfRangeOnly;
    setShowOutOfRangeOnly(newValue);
    sessionStorage.setItem('icpDetailShowOutOfRangeOnly', String(newValue));
  };

  // State for selected elements (for history comparison)
  const [selectedElements, setSelectedElements] = useState<string[]>(() => {
    try {
      const saved = sessionStorage.getItem('icpDetailSelectedElements');
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      // Type guard: ensure parsed value is a string array
      if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
        return parsed;
      }
      return [];
    } catch {
      return [];
    }
  });

  // Persist selected elements to session storage
  useEffect(() => {
    sessionStorage.setItem('icpDetailSelectedElements', JSON.stringify(selectedElements));
  }, [selectedElements]);

  // Fetch element history when elements are selected
  const {
    data: historyData,
    isLoading: historyLoading,
    error: historyError,
  } = useElementHistory(selectedElements);

  const {
    data: dosingRatesData,
  } = useElementDosingRates(historyData?.test_dates ?? []);

  // Toggle element selection
  const handleElementToggle = (elementKey: string) => {
    setSelectedElements((prev) =>
      prev.includes(elementKey)
        ? prev.filter((e) => e !== elementKey)
        : [...prev, elementKey]
    );
  };

  // Clear all selected elements
  const clearSelectedElements = () => {
    setSelectedElements([]);
    sessionStorage.removeItem('icpDetailSelectedElements');
  };

  const isParameterOutOfRange = (paramKey: string, value: number | null): boolean => {
    const range = findRange(paramKey);
    if (!range) return false; // No range = don't show when filtering
    const detailed = resolveStatus(range.provider_status, value, range.set_point_range);
    return detailed === 'above-range' || detailed === 'below-range';
  };

  // Shared filter function to avoid duplication between desktop and mobile views
  const getVisibleParams = (category: typeof PARAMETER_CATEGORIES[0]) => {
    if (!test) return [];
    return category.params.filter((param) => {
      const value = test[param.key];
      const hasValue = value !== null && value !== undefined;
      // Below-detection parameters render as "n.d." even though the stored
      // value is null (distinct from missing parameters, which stay hidden)
      if (!hasValue && !isBelowDetection(param.key)) return false;
      if (showOutOfRangeOnly) {
        return isParameterOutOfRange(
          param.key,
          typeof value === 'number' ? value : null
        );
      }
      return true;
    });
  };

  // Media query for responsive layout
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Helper function to find range for a parameter
  const findRange = (parameterKey: string): ReferenceRange | undefined => {
    // Convert database column name to ICP parameter code
    const icpCode = DB_TO_ICP_CODE[parameterKey];
    if (!icpCode) return undefined;

    return referenceRanges.find((r: ReferenceRange) => r.parameter === icpCode);
  };

  // Check if filter is active and no parameters are visible
  const hasVisibleParams = PARAMETER_CATEGORIES.some(
    (category) => getVisibleParams(category).length > 0
  );

  // Whether ANY row will render with the filter off — a param shows if it has a
  // value or is a below-detection (n.d.) reading. Used to decide the genuine
  // "no data" empty state, so a below_detection list referencing only unmappable
  // symbols (no renderable n.d. rows) doesn't silently suppress the message.
  const hasAnyRenderableParam =
    !!test &&
    PARAMETER_CATEGORIES.some((category) =>
      category.params.some((param) => {
        const value = test[param.key];
        const hasValue = value !== null && value !== undefined;
        return hasValue || isBelowDetection(param.key);
      })
    );

  // Count out-of-range parameters. Computed from the same parameter iteration
  // and resolution (`isParameterOutOfRange` → `resolveStatus`) the rows and the
  // "Show Out of Range" filter use, so the header badge can never disagree with
  // what is displayed — including ATI tests whose verdict comes from
  // `provider_status` rather than a numeric set_point_range.
  const outOfRangeCount = test
    ? PARAMETER_CATEGORIES.reduce(
        (acc, category) =>
          acc +
          category.params.filter((param) => {
            const value = test[param.key];
            const hasValue = value !== null && value !== undefined;
            if (!hasValue && !isBelowDetection(param.key)) return false;
            return isParameterOutOfRange(
              param.key,
              typeof value === 'number' ? value : null
            );
          }).length,
        0
      )
    : 0;

  // Helper to render mobile category view
  const renderMobileCategory = (category: typeof PARAMETER_CATEGORIES[0]) => {
    if (!test) return null;

    // Use shared filter function for consistency with desktop view
    const paramsWithValues = getVisibleParams(category);

    if (paramsWithValues.length === 0) return null;

    return (
      <Paper key={category.name} p={{ base: 'xs', sm: 'md' }} px={{ base: 0, sm: 'md' }} withBorder>
        <Stack gap="sm" px={{ base: 'xs', sm: 0 }}>
          <Group justify="space-between">
            <Title order={3}>{category.name}</Title>
            <Badge size="lg" variant="light" color={category.color}>
              {paramsWithValues.length} parameters
            </Badge>
          </Group>

          <Box>
            {paramsWithValues.map((param) => {
              const value = test[param.key];
              const numValue = typeof value === 'number' ? value : null;
              const belowDetection = isBelowDetection(param.key);
              const formattedValue = belowDetection
                ? 'n.d.'
                : typeof value === 'number'
                ? value.toFixed(param.decimals)
                : '-';
              const range = findRange(param.key);
              const detailedStatus = resolveStatus(
                range?.provider_status,
                numValue,
                range?.set_point_range
              );
              const status = toSimpleStatus(detailedStatus);

              return (
                <Group key={param.key} gap="xs" align="flex-start" wrap="nowrap">
                  <Checkbox
                    size="xs"
                    checked={selectedElements.includes(param.key)}
                    onChange={() => handleElementToggle(param.key)}
                    aria-label={`Compare ${param.label}`}
                    mt={4}
                  />
                  <Box style={{ flex: 1 }}>
                    <ParameterRowMobile
                      label={param.label}
                      value={numValue}
                      formattedValue={formattedValue}
                      unit={param.unit}
                      range={range}
                      status={status}
                      detailedStatus={detailedStatus}
                      categoryColor={category.color}
                      belowDetection={belowDetection}
                      rangeLabel={rangeColumnHeader}
                      showDesired={showDesiredColumn}
                      showImportance={showImportanceColumn}
                      onViewNarrative={
                        range?.narrative_text
                          ? () =>
                              setSelectedParameter({
                                name: param.label,
                                narrative: range.narrative_text || null,
                              })
                          : undefined
                      }
                    />
                    {showManualComparison && (() => {
                      const manualData = nearestManualTests?.[param.key];
                      if (!manualData) return null;
                      return (
                        <Group gap={4} mt={2} ml="sm">
                          <Text size="xs" c="dimmed">Manual:</Text>
                          <Text size="xs" fw={500}>
                            {typeof manualData.value === 'number'
                              ? manualData.value.toFixed(param.decimals)
                              : String(manualData.value)}
                          </Text>
                          <Text size="xs" c="dimmed">
                            ({formatTimeDelta(manualData.delta_seconds)})
                          </Text>
                        </Group>
                      );
                    })()}
                  </Box>
                  {(() => {
                    const analysisKey = ICP_TO_ANALYSIS_KEY[param.key];
                    if (!analysisKey || !availableSources || !test.test_date) return null;
                    const sources = availableSources[analysisKey];
                    if (!sources || sources.length === 0) return null;
                    const trendUrl = buildAnalysisTrendUrl({
                      paramKey: analysisKey,
                      sources,
                      testDate: test.test_date,
                    });
                    if (!trendUrl) return null;
                    return (
                      <ActionIcon
                        component={Link}
                        to={trendUrl}
                        variant="subtle"
                        size="sm"
                        color="gray"
                        mt={4}
                      >
                        <IconTrendingUp size={14} />
                      </ActionIcon>
                    );
                  })()}
                </Group>
              );
            })}
          </Box>
        </Stack>
      </Paper>
    );
  };

  const breadcrumbs = [
    { title: 'ICP Tests', href: '/icp-tests' },
    { title: barcode || 'Detail', href: '#' },
  ];

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Center py="xl">
          <Stack align="center" gap="md">
            <Loader size="xl" />
            <Text c="dimmed">Loading test details...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <Button
            component={Link}
            to="/icp-tests"
            leftSection={<IconArrowLeft size={16} />}
            variant="subtle"
          >
            Back to Tests
          </Button>
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error loading test details"
            color="red"
          >
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </Alert>
        </Stack>
      </Container>
    );
  }

  if (!test) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <Button
            component={Link}
            to="/icp-tests"
            leftSection={<IconArrowLeft size={16} />}
            variant="subtle"
          >
            Back to Tests
          </Button>
          <Alert icon={<IconAlertCircle size={16} />} title="Test not found" color="yellow">
            The requested ICP test could not be found.
          </Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl" px={{ base: 'xs', sm: 'md' }}>
      <Stack gap="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs>
          {breadcrumbs.map((item, index) => (
            item.href !== '#' ? (
              <Anchor
                key={index}
                component={Link}
                to={item.href}
              >
                {index === 0 && <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                {item.title}
              </Anchor>
            ) : (
              <Anchor
                key={index}
                style={{ cursor: 'default' }}
                c="dimmed"
              >
                {item.title}
              </Anchor>
            )
          ))}
        </Breadcrumbs>

        {/* Header */}
        <Paper p="md" withBorder>
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Group gap="md" align="center">
                <Title order={2}>{test.barcode}</Title>
                <ProviderBadge provider={test.provider} />
                {outOfRangeCount > 0 && (
                  <Badge size="lg" variant="filled" color="red">
                    {outOfRangeCount} out of range
                  </Badge>
                )}
              </Group>
              {test.tank_name && (
                <Badge size="lg" variant="light" color="blue">
                  {test.tank_name}
                </Badge>
              )}
              {test.source_url && /^https?:\/\//i.test(test.source_url) && (
                <Anchor
                  href={test.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                >
                  View original report ↗
                </Anchor>
              )}
              {test.test_date && !isEditingDate && (
                <Group gap={4} align="center">
                  <Text size="sm" c="dimmed">
                    Tested: {new Date(test.test_date).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="gray"
                    onClick={handleDateEdit}
                    aria-label="Edit test date"
                  >
                    <IconPencil size={14} />
                  </ActionIcon>
                </Group>
              )}
              {isEditingDate && (
                <Group gap="xs" align="flex-end">
                  <DateTimePicker
                    label="Test date & time"
                    aria-label="Test date and time"
                    value={editDate}
                    onChange={(val) => setEditDate(val ? new Date(val) : null)}
                    maxDate={new Date()}
                    valueFormat="MMM DD, YYYY hh:mm A"
                    size="sm"
                    style={{ width: 260 }}
                  />
                  <ActionIcon
                    variant="filled"
                    color="green"
                    size="sm"
                    onClick={handleDateSave}
                    loading={updateMutation.isPending}
                    aria-label="Save date"
                  >
                    <IconCheck size={14} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    onClick={handleDateCancel}
                    aria-label="Cancel edit"
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              )}
            </Stack>

            <Group gap="xs">
              {hasAnyManualData && (
                <Button
                  leftSection={<IconArrowsExchange size={16} />}
                  variant={showManualComparison ? 'filled' : 'light'}
                  color={showManualComparison ? 'blue' : 'gray'}
                  size="sm"
                  onClick={handleManualComparisonToggle}
                  loading={manualTestsLoading}
                >
                  {showManualComparison ? 'Hide Manual Tests' : 'Compare Manual Tests'}
                </Button>
              )}

              <Button
                leftSection={showOutOfRangeOnly ? <IconFilterOff size={16} /> : <IconFilter size={16} />}
                variant={showOutOfRangeOnly ? 'filled' : 'light'}
                color={showOutOfRangeOnly ? 'red' : 'gray'}
                size="sm"
                onClick={handleFilterToggle}
              >
                {showOutOfRangeOnly ? 'Show All' : 'Show Out of Range'}
              </Button>

              {test.provider === 'reef_zelements' && (
                <Tooltip label="View on Reef Zlements" position="bottom">
                  <ActionIcon
                    component="a"
                    href={`https://icp.reef-zlements.com/icp-eos-analysis/?barcode=${encodeURIComponent(barcode || '')}&tank_name=${encodeURIComponent(sanitizeName(test.tank_name))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="default"
                    size="lg"
                  >
                    <IconExternalLink size={18} color="var(--mantine-color-blue-6)" />
                  </ActionIcon>
                </Tooltip>
              )}

              <Tooltip label="Trace Dosing Calculator" position="bottom">
                <ActionIcon
                  component={Link}
                  to="/trace-dosing"
                  variant="default"
                  size="lg"
                >
                  <IconCalculator size={18} color="var(--mantine-color-blue-6)" />
                </ActionIcon>
              </Tooltip>

              <Tooltip label="Delete Test" position="bottom">
                <ActionIcon
                  variant="default"
                  size="lg"
                  onClick={handleDelete}
                >
                  <IconTrash size={18} color="var(--mantine-color-red-6)" />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        </Paper>

        {/* Element History Section */}
        <ElementHistorySection
          isOpen={selectedElements.length > 0}
          data={historyData}
          dosingRates={dosingRatesData?.rates}
          isLoading={historyLoading}
          error={historyError}
          selectedElements={selectedElements}
          onClear={clearSelectedElements}
        />

        {/* ATI Quality Scores */}
        {visibleQualityScores.length > 0 && (
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Title order={3}>Quality Scores</Title>
              <Group gap="xl" justify="center">
                {visibleQualityScores.map(({ key, label, score }) => {
                  const clampedScore = Math.max(0, Math.min(100, score));
                  return (
                  <Stack key={key} gap={4} align="center">
                    <RingProgress
                      size={90}
                      thickness={8}
                      roundCaps
                      sections={[
                        {
                          value: clampedScore,
                          color: score >= 75 ? 'teal' : score >= 50 ? 'yellow' : 'coral',
                        },
                      ]}
                      label={
                        <Text ta="center" fw={700} size="sm">
                          {clampedScore}
                        </Text>
                      }
                    />
                    <Text size="xs" c="dimmed">
                      {label}
                    </Text>
                  </Stack>
                  );
                })}
              </Group>
            </Stack>
          </Paper>
        )}

        {/* Parameter Tables by Category */}
        {PARAMETER_CATEGORIES.map((category) => {
          // Use mobile layout for smaller screens
          if (isMobile) {
            return renderMobileCategory(category);
          }

          // Desktop table layout
          // Filter parameters that have values (and optionally only out-of-range)
          const paramsWithValues = getVisibleParams(category);

          // Skip empty categories
          if (paramsWithValues.length === 0) {
            return null;
          }

          return (
            <Paper key={category.name} p="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between">
                  <Title order={3}>{category.name}</Title>
                  <Badge size="lg" variant="light" color={category.color}>
                    {paramsWithValues.length} parameters
                  </Badge>
                </Group>

                <ScrollArea>
                  <Table striped highlightOnHover withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 40 }}>
                          <Text size="xs" c="dimmed">Compare</Text>
                        </Table.Th>
                        <Table.Th style={{ width: 40 }}></Table.Th>
                        <Table.Th>Parameter</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Value</Table.Th>
                        <Table.Th>Unit</Table.Th>
                        {showManualComparison && (
                          <Table.Th style={{ textAlign: 'right', minWidth: 120 }}>Manual Test</Table.Th>
                        )}
                        <Table.Th>{rangeColumnHeader}</Table.Th>
                        {showDesiredColumn && <Table.Th>Desired</Table.Th>}
                        <Table.Th style={{ minWidth: 180 }}>Visual</Table.Th>
                        <Table.Th style={{ minWidth: 150 }}>Dosing Recommendation</Table.Th>
                        {showImportanceColumn && <Table.Th style={{ width: 100 }}>Importance</Table.Th>}
                        <Table.Th style={{ width: 50 }}></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {paramsWithValues.map((param) => {
                        const value = test[param.key];
                        const numValue = typeof value === 'number' ? value : null;
                        const belowDetection = isBelowDetection(param.key);
                        const formattedValue =
                          typeof value === 'number'
                            ? value.toFixed(param.decimals)
                            : '-';

                        // Find reference range for this parameter
                        const range = findRange(param.key);
                        const detailedStatus = resolveStatus(
                          range?.provider_status,
                          numValue,
                          range?.set_point_range
                        );
                        const status = toSimpleStatus(detailedStatus);

                        return (
                          <Table.Tr key={param.key}>
                            {/* Checkbox for element selection */}
                            <Table.Td>
                              <Checkbox
                                size="xs"
                                checked={selectedElements.includes(param.key)}
                                onChange={() => handleElementToggle(param.key)}
                                aria-label={`Compare ${param.label}`}
                              />
                            </Table.Td>

                            {/* Status Indicator */}
                            <Table.Td>
                              {detailedStatus === 'in-range' && (
                                <IconCircleFilled size={12} color="var(--mantine-color-green-6)" />
                              )}
                              {detailedStatus === 'above-range' && (
                                <IconArrowUp size={14} color="var(--mantine-color-red-6)" stroke={3} />
                              )}
                              {detailedStatus === 'below-range' && (
                                <IconArrowDown size={14} color="var(--mantine-color-red-6)" stroke={3} />
                              )}
                              {detailedStatus === 'no-range' && (
                                <IconCircleFilled size={12} color="var(--mantine-color-gray-4)" />
                              )}
                            </Table.Td>

                            {/* Parameter */}
                            <Table.Td>
                              {range?.narrative_text ? (
                                <Text
                                  size="sm"
                                  fw={500}
                                  c="blue.6"
                                  style={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    transition: 'text-decoration-style 0.1s',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecorationStyle = 'solid';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecorationStyle = 'dotted';
                                  }}
                                  onClick={() =>
                                    setSelectedParameter({
                                      name: param.label,
                                      narrative: range.narrative_text || null,
                                    })
                                  }
                                >
                                  {param.label}
                                </Text>
                              ) : (
                                <Text size="sm" fw={500}>
                                  {param.label}
                                </Text>
                              )}
                            </Table.Td>

                            {/* Value */}
                            <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {belowDetection ? (
                                <Tooltip label={BELOW_DETECTION_TOOLTIP}>
                                  <Text size="sm" fw={700} c="dimmed">
                                    n.d.
                                  </Text>
                                </Tooltip>
                              ) : (
                                <Text
                                  size="sm"
                                  fw={700}
                                  c={status === 'out-of-range' ? 'red' : category.color}
                                >
                                  {formattedValue}
                                </Text>
                              )}
                            </Table.Td>

                            {/* Unit */}
                            <Table.Td>
                              <Text size="sm" c="dimmed">
                                {param.unit || '-'}
                              </Text>
                            </Table.Td>

                            {/* Manual Test Comparison */}
                            {showManualComparison && (
                              <Table.Td style={{ textAlign: 'right' }}>
                                {(() => {
                                  const manualData = nearestManualTests?.[param.key];
                                  if (!manualData) {
                                    return <Text size="sm" c="dimmed">--</Text>;
                                  }
                                  return (
                                    <div>
                                      <Text size="sm" fw={500} style={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {typeof manualData.value === 'number'
                                          ? manualData.value.toFixed(param.decimals)
                                          : String(manualData.value)}
                                      </Text>
                                      <Text size="xs" c="dimmed">
                                        {formatTimeDelta(manualData.delta_seconds)}
                                      </Text>
                                    </div>
                                  );
                                })()}
                              </Table.Td>
                            )}

                            {/* Set Point Range / Ideal Value (whichever exists) */}
                            <Table.Td>
                              <Text size="xs" c="dimmed">
                                {range?.set_point_range || range?.desired_level || '-'}
                              </Text>
                            </Table.Td>

                            {/* Desired */}
                            {showDesiredColumn && (
                              <Table.Td>
                                <Text size="xs" c="dimmed">
                                  {range?.desired_level || '-'}
                                </Text>
                              </Table.Td>
                            )}

                            {/* Visual Range Bar */}
                            <Table.Td>
                              {numValue !== null && range ? (
                                <RangeBar
                                  value={numValue}
                                  rangeStr={range.set_point_range}
                                  desiredStr={range.desired_level}
                                  inRange={status === 'in-range'}
                                />
                              ) : (
                                <Text size="xs" c="dimmed">
                                  -
                                </Text>
                              )}
                            </Table.Td>

                            {/* Dosing Recommendation */}
                            <Table.Td>
                              <Text size="xs" c={range?.dosing_recommendation ? 'blue' : 'dimmed'}>
                                {range?.dosing_recommendation || '-'}
                              </Text>
                            </Table.Td>

                            {/* Importance (Reef Zelements only) */}
                            {showImportanceColumn && (
                              <Table.Td>
                                {range?.importance && (
                                  <Badge
                                    size="sm"
                                    variant="light"
                                    color={
                                      range.importance.toLowerCase().includes('high')
                                        ? 'red'
                                        : range.importance.toLowerCase().includes('medium')
                                        ? 'yellow'
                                        : 'gray'
                                    }
                                  >
                                    {range.importance}
                                  </Badge>
                                )}
                                {!range?.importance && (
                                  <Text size="xs" c="dimmed">
                                    -
                                  </Text>
                                )}
                              </Table.Td>
                            )}

                            {/* View Trend */}
                            <Table.Td>
                              {(() => {
                                const analysisKey = ICP_TO_ANALYSIS_KEY[param.key];
                                if (!analysisKey || !availableSources || !test.test_date) return null;
                                const sources = availableSources[analysisKey];
                                if (!sources || sources.length === 0) return null;
                                const trendUrl = buildAnalysisTrendUrl({
                                  paramKey: analysisKey,
                                  sources,
                                  testDate: test.test_date,
                                });
                                if (!trendUrl) return null;
                                return (
                                  <Tooltip label="View Trend" position="left">
                                    <ActionIcon
                                      component={Link}
                                      to={trendUrl}
                                      variant="subtle"
                                      size="sm"
                                      color="gray"
                                    >
                                      <IconTrendingUp size={16} />
                                    </ActionIcon>
                                  </Tooltip>
                                );
                              })()}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Stack>
            </Paper>
          );
        })}

        {/* Empty State - Filter Active but All Parameters In Range */}
        {showOutOfRangeOnly && hasAnyRenderableParam && !hasVisibleParams && (
          <Paper p="xl" ta="center" c="dimmed">
            <Text size="lg">No out-of-range parameters found</Text>
            <Text size="sm" mt="xs">All parameters are within their target ranges</Text>
          </Paper>
        )}

        {/* Empty State - No Data Available (no values AND no renderable n.d. rows) */}
        {!hasAnyRenderableParam && (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow">
            No parameter data available for this test.
          </Alert>
        )}
      </Stack>

      {/* Parameter Narrative Modal */}
      <ParameterNarrativeModal
        isOpen={selectedParameter !== null}
        parameterName={selectedParameter?.name || ''}
        narrativeText={selectedParameter?.narrative || null}
        onClose={() => setSelectedParameter(null)}
      />
    </Container>
  );
}
